import { useQuery } from '@apollo/client';
import { Delta } from 'quill/core';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import QuillComponent from '../editor/QuillComponent';
import QuillContext from '../editor/QuillContext';
import { gql } from '../graphql/generated/gql';
import * as styles from './ComparePostsView.module.scss';
import HoverContentPreview from './HoverContentPreview';

const COMPARE_POSTS_VIEW_QUERY = gql(`
  query ComparePostsView($basePostId: ID!, $comparePostId: ID!) {
    basePost: post(id: $basePostId) {
      id
      content
      version
    }
    comparePost: post(id: $comparePostId) {
      id
      content
      version
    }
  }
`);

function getDiff(baseContent: Delta, compareContent: Delta) {
  const diff = baseContent.diff(compareContent).map((op) => {
    if (op.insert) {
      return {
        ...op,
        attributes: { ...op.attributes, diff: 'inserted' },
      };
    }

    if (op.delete) {
      return {
        retain: op.delete,
        attributes: { ...op.attributes, diff: 'deleted' },
      };
    }

    return op;
  });

  return new Delta(diff);
}

const ComparePostsView = () => {
  const { basePostId, comparePostId } = useParams() as {
    basePostId: string;
    comparePostId: string;
  };

  const { data } = useQuery(COMPARE_POSTS_VIEW_QUERY, {
    fetchPolicy: 'network-only',
    variables: { basePostId, comparePostId },
  });

  const contentWithDiff = useMemo(() => {
    if (data?.comparePost && data?.basePost) {
      return new Delta(data.basePost.content).compose(
        getDiff(
          new Delta(data.basePost.content),
          new Delta(data.comparePost.content),
        ),
      );
    }
    return null;
  }, [data]);

  if (contentWithDiff == null) return null;

  return (
    <div className={styles.container}>
      <QuillContext>
        <HoverContentPreview>
          <QuillComponent
            rootClassName={styles.editorRoot}
            containerClassName={styles.editorContainer}
            namespace="history"
            initialContent={contentWithDiff}
            readOnly
          />
        </HoverContentPreview>
      </QuillContext>
    </div>
  );
};

export default ComparePostsView;
